/**
 * ApolloSpendWidget — Phase 2 (Iter 55).
 *
 * Polls /api/ai/ops/apollo-spend every 60s and renders a compact card on the
 * Governor dashboard showing real-time Apollo API spend, broken down by:
 *   - Total calls
 *   - Your Apollo wholesale cost (provider_cost_usd)
 *   - Tenant-charged revenue (tenant_charged_usd)
 *   - Platform-absorbed CAC (the gap)
 *   - Top 5 spenders by tenant
 *
 * Mounted on the Governor Dashboard next to StuckJobsWidget. Tenant-facing
 * pages do NOT see this. Read-only.
 */
import { useCallback, useEffect, useState } from 'react';
import { governanceAPI } from '../../../lib/governanceApi';
import { useSuperAdminTheme } from '../../../contexts/SuperAdminThemeContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { RefreshCw, Search, Phone, Building2, AlertTriangle } from 'lucide-react';

const POLL_MS = 60_000;

const ACTION_ICON = {
  apollo_search: Search,
  apollo_reveal: Phone,
  apollo_enrichment: Building2,
};

function fmtUSD(n) {
  if (n === null || n === undefined || isNaN(n)) return '$0.00';
  return `$${Number(n).toFixed(2)}`;
}

export default function ApolloSpendWidget({ days = 7 }) {
  const { theme } = useSuperAdminTheme();
  const isDark = theme === 'dark';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const fetchSpend = useCallback(async () => {
    try {
      setErr(null);
      const res = await governanceAPI.getApolloSpend(days);
      setData(res.data);
    } catch (e) {
      console.warn('[ApolloSpend] fetch failed', e);
      setErr(e?.response?.data?.detail || 'Failed to load Apollo spend');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    fetchSpend();
    const t = setInterval(fetchSpend, POLL_MS);
    return () => clearInterval(t);
  }, [fetchSpend]);

  const cardClass = isDark ? 'bg-slate-900/60 border-slate-800' : 'bg-white border-slate-200';
  const textMuted = isDark ? 'text-slate-400' : 'text-slate-500';
  const textBody = isDark ? 'text-slate-100' : 'text-slate-900';

  return (
    <Card className={cardClass} data-testid="apollo-spend-widget">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className={`text-sm font-semibold uppercase tracking-wider ${textMuted}`}>
            Apollo Spend (last {days}d)
          </CardTitle>
          <button
            onClick={fetchSpend}
            disabled={loading}
            className={`inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md transition ${isDark ? 'text-slate-400 hover:text-slate-100 hover:bg-slate-800' : 'text-slate-500 hover:text-slate-800 hover:bg-slate-100'} disabled:opacity-40`}
            data-testid="apollo-spend-refresh"
            title="Refresh"
          >
            <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {err ? (
          <div className="flex items-center gap-2 text-xs text-amber-500" data-testid="apollo-spend-error">
            <AlertTriangle className="w-3.5 h-3.5" />
            <span>{err}</span>
          </div>
        ) : !data ? (
          <div className={`text-xs ${textMuted}`} data-testid="apollo-spend-loading">Loading…</div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3">
              <Stat label="Calls" value={data.total_calls ?? 0} testid="apollo-spend-calls" isDark={isDark} />
              <Stat label="Provider cost" value={fmtUSD(data.total_provider_cost_usd)} testid="apollo-spend-provider-cost" isDark={isDark} />
              <Stat label="Tenant charged" value={fmtUSD(data.total_tenant_charged_usd)} testid="apollo-spend-tenant-charged" isDark={isDark} />
              <Stat label="Platform absorbed" value={fmtUSD(data.total_platform_absorbed_usd)} testid="apollo-spend-platform-absorbed" warn={data.total_platform_absorbed_usd > 5} isDark={isDark} />
            </div>

            <div>
              <div className={`text-[10px] uppercase tracking-wider font-semibold mb-1.5 ${textMuted}`}>By call type</div>
              {(data.summary || []).length === 0 ? (
                <div className={`text-xs ${textMuted}`} data-testid="apollo-spend-empty">No Apollo activity yet</div>
              ) : (
                <ul className="space-y-1">
                  {(data.summary || []).slice(0, 6).map((s, idx) => {
                    const Icon = ACTION_ICON[s.action_type] || Search;
                    // Iter 62.6 — payer "platform" is a system placeholder
                    // for calls absorbed by Lockene itself; rendering it as a
                    // badge looks like leaked debug info. Show a clean label
                    // ("absorbed") for the platform payer; hide for tenant
                    // payers since the tenant_id list below already explains who.
                    const isPlatform = s.payer === 'platform';
                    const payerLabel = isPlatform ? 'absorbed' : null;
                    return (
                      <li
                        key={`${s.action_type}-${s.payer}-${idx}`}
                        className="flex items-center justify-between text-xs"
                        data-testid={`apollo-spend-row-${s.action_type}-${s.payer}`}
                      >
                        <span className={`inline-flex items-center gap-1.5 ${textBody}`}>
                          <Icon className="w-3 h-3 opacity-70" />
                          <span className="capitalize">{s.action_type.replace('apollo_', '')}</span>
                          {payerLabel && (
                            <Badge variant="outline" className="text-[10px] px-1 py-0 leading-tight font-normal">
                              {payerLabel}
                            </Badge>
                          )}
                        </span>
                        <span className={textMuted}>
                          {s.calls} · {fmtUSD(s.provider_cost_usd)}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            {(data.top_tenants || []).length > 0 && (
              <div>
                <div className={`text-[10px] uppercase tracking-wider font-semibold mb-1.5 ${textMuted}`}>Top spenders</div>
                <ul className="space-y-1">
                  {(data.top_tenants || []).slice(0, 5).map((t, idx) => {
                    const isPlatform = t.tenant_id === 'platform';
                    return (
                      <li
                        key={t.tenant_id || idx}
                        className="flex items-center justify-between text-xs"
                        data-testid={`apollo-spend-tenant-${idx}`}
                      >
                        <span className={`${isPlatform ? '' : 'font-mono'} ${textBody} truncate max-w-[200px] inline-flex items-center gap-1.5`} title={t.tenant_id}>
                          {isPlatform ? (
                            <>
                              <span>Platform-absorbed</span>
                              <Badge variant="outline" className="text-[9px] px-1 py-0 leading-tight font-normal opacity-70">internal</Badge>
                            </>
                          ) : (
                            t.tenant_id.slice(0, 8) + '…'
                          )}
                        </span>
                        <span className={textMuted}>
                          {t.calls} · {fmtUSD(t.provider_cost_usd)}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function Stat({ label, value, testid, warn = false, isDark }) {
  const labelCls = isDark ? 'text-slate-500' : 'text-slate-500';
  const valueCls = warn ? 'text-amber-500' : isDark ? 'text-slate-100' : 'text-slate-900';
  return (
    <div data-testid={testid} className={isDark ? 'bg-slate-950/50 rounded-md px-3 py-2 border border-slate-800' : 'bg-slate-50 rounded-md px-3 py-2 border border-slate-200'}>
      <div className={`text-[10px] uppercase tracking-wider ${labelCls}`}>{label}</div>
      <div className={`text-base font-semibold ${valueCls}`}>{value}</div>
    </div>
  );
}
